import React, { useCallback, useState } from 'react';
import fileSaver from 'file-saver';
import Tooltip from '@material-ui/core/Tooltip';

import {
    ALLOWED_CERT_CONTENT_TYPES_TO_IMPORT,
    ALLOWED_CERT_EXTENTIONS_TO_IMPORT,
    MAX_FILE_NAME_LENGTH_TO_IMPORT,
    SERVER_TYPE
} from '@silverfort-nodelibs/common/utils/consts';

import Button from '../Button/Button';
import Description from '../Description/Description';
import Label from '../Label/Label';
import CertificateRequestPopup from './CertificateRequestPopup';
import UploadButton from '../UploadButton/UploadButton';
import ConfirmationMessage from '../../../../Controls/ConfirmationMessage';

import styles from './Certificate.module.css';
import CopyButton from '../CopyButton/CopyButton';
import { httpPost } from '../../../../utils';

const Certificate = ({
    id,
    header,
    desc,
    certificate,
    certificateRequest,
    certType,
    serverType,
    allowDelete,
    generateCSR = true,
    changed,
    baseName = ''
}) => {
    const [openModal, setOpenModal] = useState(false);
    const [error, setError] = useState('');
    const [showDeleteMessage, setShowDeleteMessage] = useState(false);

    const fqdnText = serverType === SERVER_TYPE.DOMAIN_ROOT_CA ? 'Common Name:' : 'Server FQDN:';
    const togglePopup = useCallback(() => {
        setOpenModal(!openModal);
    }, [openModal, setOpenModal]);

    const handleDeleteClicked = () => {
        setShowDeleteMessage(true);
    };

    const onCSRSave = (csrData, serverFQDN, customSAN) => {
        setError('');
        setOpenModal(false);
        changed(certificateRequest.id, { data: csrData, fqdn: serverFQDN, san: customSAN });
    };

    const downloadCertificate = async () => {
        setError('');
        const postData = {
            serverType: serverType
        };

        let name = 'silverfort';
        if (
            serverType === SERVER_TYPE.NODE ||
            serverType === SERVER_TYPE.LDAPS ||
            serverType === SERVER_TYPE.DOMAIN_ROOT_CA
        ) {
            postData.domainID = id;
            name = baseName;
        } else if (serverType === SERVER_TYPE.DC) {
            postData.dcGuid = id;
            name = baseName;
        } else if (serverType === SERVER_TYPE.ARYA && id) {
            postData.arayID = id;
            name = baseName;
        }

        try {
            const { data } = await httpPost('/Settings/DownloadCertificate', postData, {
                responseType: 'arraybuffer'
            });
            const blob = new Blob([data], { type: 'octet/stream' });
            const filename = `${name}-certificate.crt`;
            fileSaver.saveAs(blob, filename);
        } catch (err) {
            console.error(err);
            setError('Failed to download certificate');
        }
    };

    const uploadCertificate = async (files) => {
        setError('');
        const file = files[0];

        // Get the extension of the uploaded file
        const file_extension = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();

        // Check if the uploaded file is allowed
        if (
            !ALLOWED_CERT_EXTENTIONS_TO_IMPORT.includes(file_extension) ||
            !ALLOWED_CERT_CONTENT_TYPES_TO_IMPORT.includes(file.type)
        ) {
            setError('Invalid certificate');
            return;
        } else if (file.name.length > MAX_FILE_NAME_LENGTH_TO_IMPORT) {
            setError(`File name should be up to ${MAX_FILE_NAME_LENGTH_TO_IMPORT} characters`);
            return;
        }

        let requestUrl = '/Settings/UploadCert';
        if (
            serverType === SERVER_TYPE.NODE ||
            serverType === SERVER_TYPE.LDAPS ||
            serverType === SERVER_TYPE.DOMAIN_ROOT_CA
        ) {
            requestUrl = `/Settings/UploadDomainCert/${id}/${serverType}`;
        } else if (serverType === SERVER_TYPE.ARYA && !id) {
            requestUrl = '/Settings/uploadPushServicesCert';
        } else if (serverType === SERVER_TYPE.ARYA && id) {
            requestUrl = `/Settings/uploadPushServicesCustomCert/${id}`;
        } else if (serverType === SERVER_TYPE.SSO) {
            requestUrl = '/Settings/uploadSSOCert';
        }

        const data = new FormData();
        data.append('file', file);

        try {
            const response = await httpPost(requestUrl, data);
            const { certificateData, fqdn, san, expire } = response.data;
            if (!certificateData) {
                return Promise.reject('No certificate data');
            }

            changed(certificate.id, {
                data: certificateData,
                fqdn,
                san,
                expire
            });
        } catch (err) {
            console.error(err);
            setError('Failed to upload certificate');
        }
    };

    const deleteCertificate = () => {
        changed(certificate.id, '');
        setShowDeleteMessage(false);
    };

    const renderTooltip = (certField) => {
        return (
            <div>
                {certField.value.fqdn && (
                    <div className={styles.decodedLine}>
                        <div className={styles.boldText}>{fqdnText}</div>
                        {certField.value.fqdn}
                    </div>
                )}
                {certField.value.expire && (
                    <div className={styles.decodedLine}>
                        <div className={styles.boldText}>Expiration:</div>
                        {certField.value.expire}
                    </div>
                )}
                {certField.value.san && (
                    <div className={styles.decodedLine}>
                        <div className={styles.boldText}>Custom SAN:</div>
                        <ul>
                            {certField.value.san.split('\n').map((s, i) => (
                                <li key={s + i}>{s}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    const shouldHaveTooltip = (certField) => {
        return certField.value?.fqdn || certField.value?.expire || certField.value?.san;
    };

    return (
        <>
            <Label value={header} />
            {desc && <Description value={desc} />}
            <div className={styles.container}>
                {generateCSR && (
                    <>
                        <div>
                            <Button onClick={togglePopup}>
                                <i className="fa fa-cog" />
                                <span className={styles.buttonText}>Generate Certificate Request</span>
                            </Button>
                            <div className={styles.buttonButtonContainer}>
                                <CopyButton value={certificateRequest.value.data} />
                                <div>
                                    {certificateRequest.value.fqdn && (
                                        <Tooltip
                                            disableHoverListener={!shouldHaveTooltip(certificateRequest)}
                                            disabled={!certificateRequest.value.san}
                                            title={renderTooltip(certificateRequest)}
                                            arrow
                                            enterDelay={300}
                                            classes={{ tooltip: 'settings-tooltip' }}>
                                            <div>
                                                <Description value={fqdnText} />
                                                <Description value={certificateRequest.value.fqdn} />
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={styles.divider}></div>
                    </>
                )}
                <div>
                    <UploadButton
                        accept={ALLOWED_CERT_CONTENT_TYPES_TO_IMPORT.toString()}
                        text="Upload Certificate"
                        onUpload={uploadCertificate}
                    />
                    {allowDelete && certificate.value.data && (
                        <Button onClick={handleDeleteClicked} style={{ margin: '0 30px' }} deleteButton>
                            Remove Certificate
                        </Button>
                    )}
                    <div className={styles.buttonButtonContainer}>
                        <Button disabled={!certificate.value.data} onClick={downloadCertificate}>
                            <i className="fa fa-download" />
                            <span className={styles.buttonText}>Download</span>
                        </Button>
                        <div>
                            <Tooltip
                                placement={'right'}
                                disableHoverListener={!shouldHaveTooltip(certificate)}
                                disabled={!certificate.value.san}
                                title={renderTooltip(certificate)}
                                arrow
                                enterDelay={300}
                                classes={{ tooltip: 'settings-tooltip' }}>
                                <div>
                                    {certificate.value.fqdn && (
                                        <Description value={`${fqdnText} ${certificate.value.fqdn}`} />
                                    )}
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </div>
            {certificateRequest && certificateRequest.notification && (
                <div className={styles.notificationText}>{certificateRequest.notification}</div>
            )}
            {certificate && certificate.notification && (
                <div className={styles.notificationText}>{certificate.notification}</div>
            )}
            {(error || certificate.error) && <div className={styles.errorText}>{error || certificate.error}</div>}
            {openModal && (
                <CertificateRequestPopup id={id} header={header} onCancel={togglePopup} onSave={onCSRSave} certType={certType} />
            )}
            {showDeleteMessage && (
                <ConfirmationMessage
                    title={'Delete Certificate'}
                    body={`Are you sure you want to remove the ${certificate.value.fqdn} certificate?`}
                    approveText={'Delete'}
                    cancelText={'Cancel'}
                    approveStyle={'danger'}
                    OnCancel={() => setShowDeleteMessage(false)}
                    OnApprove={deleteCertificate}
                />
            )}
        </>
    );
};

export default React.memo(Certificate);
